import type { Attendee, BookedLesson } from './use-bookings.js';

export type Outcome = 'Booked' | 'Completed' | 'No show' | 'Cancelled';

/**
 * What a row says happened.
 *
 * Every row carries its own outcome, because a booking that quietly vanishes
 * from a list of twelve is how somebody ends up ringing to ask what happened to
 * week six. A cancelled lesson stays in the list, saying it was cancelled.
 *
 * "Comp" is on the mockup and is NOT here: lesson.status allows scheduled,
 * in_progress, completed and cancelled, and nothing in the schema records a
 * comped lesson yet (LS-10). It arrives when something can set it.
 *
 * A booking of two children reads from both, not from whichever lesson was made
 * first: one cancelled and one who took part is Completed (ruled 13 October,
 * found on the phone — the row said Cancelled, and on another booking Booked,
 * for a lesson that happened). The order is what took place, then what is still
 * to come, then a no-show, and Cancelled only when every child was.
 */
const PRECEDENCE: Outcome[] = ['Completed', 'Booked', 'No show', 'Cancelled'];

export function outcomeOf(lesson: BookedLesson): Outcome {
  if (lesson.booking_status === 'cancelled') return 'Cancelled';
  if (lesson.booking_status === 'no_show') return 'No show';
  if (lesson.attendees.length === 0) {
    return lesson.lesson_status === 'completed' ? 'Completed' : 'Booked';
  }
  const each = lesson.attendees.map((attendee) => attendeeOutcome(lesson, attendee));
  return PRECEDENCE.find((outcome) => each.includes(outcome)) ?? 'Booked';
}

/**
 * What happened to ONE athlete's lesson, for the screens that are about one
 * child. A booking can hold two children who ended differently — one cancelled,
 * one took part — so the booking's outcome is not theirs.
 *
 * A no-show closes the lesson as cancelled with nobody named as cancelling it,
 * which is the line between it and a cancellation.
 */
export function attendeeOutcome(lesson: BookedLesson, attendee: Attendee): Outcome {
  if (attendee.cancellation) return 'Cancelled';
  if (lesson.booking_status === 'cancelled') return 'Cancelled';
  if (attendee.lesson_status === 'cancelled') return 'No show';
  if (attendee.lesson_status === 'completed') return 'Completed';
  return 'Booked';
}
